import {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useIntl} from 'react-intl';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import OtcInput from '@components/shared/OtcInput/OtcInput';
import {AppInput} from '@components/Core/inputs/AppInput/AppInput';
import BackButton from '@components/common/BackButton/BackButton';
import {
    verifyTwoFactorLogin,
    clearTwoFactorChallenge,
    selectIsAuthenticating,
    selectAuthError
} from '../../userSlice';
import TwoFactorResetForm from './TwoFactorResetForm/TwoFactorResetForm';
import './twoFactorForm.less';

const TWO_FACTOR_CODE_LENGTH = 6;
const BACKUP_CODE_MIN_LENGTH = 10;

export default function TwoFactorForm({onSuccess, email}) {
    const intl = useIntl();
    const dispatch = useDispatch();
    const isAuthenticating = useSelector(selectIsAuthenticating);
    const authError = useSelector(selectAuthError);
    const [code, setCode] = useState('');
    const [isBackupCodeMode, setIsBackupCodeMode] = useState(false);
    const [isResetMode, setIsResetMode] = useState(false);

    function handleCodeChange(newCode) {
        setCode(newCode);
    }

    function handleBackupCodeChange(event) {
        setCode(event.target.value);
    }

    function handleBackToLogin() {
        dispatch(clearTwoFactorChallenge());
        setCode('');
        setIsBackupCodeMode(false);
    }

    function handleSwitchMode() {
        setCode('');
        setIsBackupCodeMode(!isBackupCodeMode);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const wasVerifySuccessful = await dispatch(verifyTwoFactorLogin(code));
        if (wasVerifySuccessful) {
            onSuccess();
        }
    }

    const isAuthenticatorSubmitDisabled = code.length !== TWO_FACTOR_CODE_LENGTH || isAuthenticating;
    const isBackupSubmitDisabled = code.length < BACKUP_CODE_MIN_LENGTH || isAuthenticating;
    const isSubmitDisabled = isBackupCodeMode ? isBackupSubmitDisabled : isAuthenticatorSubmitDisabled;

    if (isResetMode) {
        return (
            <div className="two-factor-form">
                <TwoFactorResetForm
                    email={email || ''}
                    onBack={() => setIsResetMode(false)}
                    onBackToLogin={handleBackToLogin}
                />
            </div>
        );
    }

    if (isBackupCodeMode) {
        return (
            <form className="two-factor-form" onSubmit={handleSubmit}>
                <BackButton onClick={handleBackToLogin} />
                <p className="two-factor-description">
                    {intl.formatMessage({
                        id: 'page.account.twoFactorAuth.enterBackupCodeDescription',
                        defaultMessage: 'Enter your backup code'
                    })}
                    {' '}
                    {intl.formatMessage({id: 'esl.generic.or', defaultMessage: 'or'})}
                    {' '}
                    <button
                        type="button"
                        className="switch-mode-link"
                        onClick={handleSwitchMode}
                    >
                        {intl.formatMessage({
                            id: 'page.account.twoFactorAuth.useAuthenticatorCode',
                            defaultMessage: 'authenticator code'
                        })}
                    </button>
                </p>

                <AppInput
                    type="text"
                    className="backup-code-input"
                    value={code}
                    onChange={handleBackupCodeChange}
                    disabled={isAuthenticating}
                    autoFocus
                    autoComplete="one-time-code"
                    dataWdio="test-backup-code-input"
                />

                <AppButton
                    type="submit"
                    disabled={isSubmitDisabled}
                    size="small"
                    className="two-factor-submit-button"
                    data-wdio="test-two-factor-submit-button"
                    fullWidth
                >
                    {intl.formatMessage({id: 'esl.generic.login', defaultMessage: 'Login'})}
                </AppButton>

                <button
                    type="button"
                    className="lost-access-link"
                    onClick={() => setIsResetMode(true)}
                    data-wdio="test-lost-2fa-access-link"
                >
                    {intl.formatMessage({
                        id: 'page.account.twoFactorAuth.lostAccess',
                        defaultMessage: 'Lost access to your authenticator?'
                    })}
                </button>
            </form>
        );
    }

    return (
        <form className="two-factor-form" onSubmit={handleSubmit}>
            <BackButton onClick={handleBackToLogin} />
            <p className="two-factor-description">
                {intl.formatMessage({
                    id: 'page.account.twoFactorAuth.enterCodeDescription',
                    defaultMessage: 'Enter the 6-digit code from your authenticator app'
                })}
                {' '}
                {intl.formatMessage({id: 'esl.generic.or', defaultMessage: 'or'})}
                {' '}
                <button
                    type="button"
                    className="switch-mode-link"
                    onClick={handleSwitchMode}
                >
                    {intl.formatMessage({
                        id: 'page.account.twoFactorAuth.useBackupCode',
                        defaultMessage: 'backup code'
                    })}
                </button>
            </p>

            <OtcInput
                value={code}
                onChange={handleCodeChange}
                digitCount={TWO_FACTOR_CODE_LENGTH}
                isDisabled={isAuthenticating}
                hasError={Boolean(authError)}
                isFullWidth
                autoFocus
            />

            <AppButton
                type="submit"
                disabled={isSubmitDisabled}
                size="small"
                className="two-factor-submit-button"
                data-wdio="test-two-factor-submit-button"
                fullWidth
            >
                {intl.formatMessage({id: 'esl.generic.login', defaultMessage: 'Login'})}
            </AppButton>

            <button
                type="button"
                className="lost-access-link"
                onClick={() => setIsResetMode(true)}
                data-wdio="test-lost-2fa-access-link"
            >
                {intl.formatMessage({
                    id: 'page.account.twoFactorAuth.lostAccess',
                    defaultMessage: 'Lost access to your authenticator?'
                })}
            </button>
        </form>
    );
}
